import ConfiguratorComponent from './modules/configurator-component';
import PriceCalculation from './modules/price-calculation-curtain';

if (!customElements.get('curtain-configurator')) {
  class CurtainConfigurator extends ConfiguratorComponent {
    constructor() {
      super();

      // Selectors specific to the curtain configurator
      this.sheetingPicker = document.querySelector('sheeting-picker');
      this.sheetingPlaceholder = document.querySelector('[data-sheeting-placeholder]');

      // Options
      this.options = {
        debug: false,
        steps: ['size', 'pleating', 'partition', 'sheeting_type', 'sheeting_id', 'quantity'],
        progressBarSteps: ['size', 'pleating', 'partition', 'sheeting'],
      };

      // state
      this.state = {
        step: 'size',
        progressBar: '',
      };

      // Get additional metaobject data
      this.additionalData = this.getAdditionalData();
      if (this.options.debug) console.log('additionalData', this.additionalData);

      // Free text input for location
      this.locationFreeInput = handleize(this.dataset.locationFreeInput || '');
    }

    /**
     * Calculate curtain price
     * @returns {object} calculation
     */
    priceCalculation() {
      return new PriceCalculation(this.productData, this.additionalData, this.formData);
    }

    /**
     * @returns additional metaobject data
     */
    getAdditionalData() {
      const materialFactorData = this.querySelector('[data-material-factor-json]')
        ? parseJson(this.querySelector('[data-material-factor-json]').textContent)
        : {};
      const manuFactureCostsData = this.querySelector('[data-manufacture-costs-json]')
        ? parseJson(this.querySelector('[data-manufacture-costs-json]').textContent)
        : {};
      const surchargesData = this.querySelector('[data-surcharges-json]')
        ? parseJson(this.querySelector('[data-surcharges-json]').textContent)
        : {};
      const sheetingsData = this.querySelector('[data-sheetings-json]')
        ? parseJson(this.querySelector('[data-sheetings-json]').textContent)
        : {};

      return {
        factors: materialFactorData,
        manufactures: manuFactureCostsData,
        surcharges: surchargesData,
        sheetings: sheetingsData,
      };
    }

    /**
     * Format add to cart item with properties
     * @param {object} calculation
     * @returns {object} line item with properties
     */
    formatLineItem(calculation) {
      if (!this.formData || !calculation) {
        return;
      }

      // Return formatted line item with properties
      return {
        id: parseInt(this.formData.id),
        quantity: parseInt(this.formData.quantity),
        properties: {
          _ref: new Date().getTime(),
          _token: calculation.token,
          _compare_at_price: calculation.compare_at_price,
          _width: this.formData.width,
          _height: this.formData.height,
          ...(this.formData.fabric_type && {
            _fabric_type: this.formData.fabric_type,
          }),
          ...(this.formData.product_type && {
            _product_type: this.formData.product_type,
          }),
          ...(this.formData.color && {
            _color: this.formData.color,
          }),
          ...(this.formData.pleating && {
            _pleating: this.formData.pleating,
          }),
          ...(this.formData.partition && {
            _partition: this.formData.partition,
          }),
          ...(this.formData.sheeting_type && {
            _sheeting_type: this.formData.sheeting_type,
          }),
          ...(this.formData.sheeting_id && {
            _sheeting_id: this.formData.sheeting_id,
          }),
          ...(this.formData.sheeting_sku && {
            _sheeting_sku: this.formData.sheeting_sku,
          }),
          ...(this.formData.sheeting_title && {
            _sheeting_title: this.formData.sheeting_title,
          }),
          ...(this.formData.sheeting_color && {
            _sheeting_color: this.formData.sheeting_color,
          }),
          ...(this.formData.delivery_time && {
            _delivery_time: this.formData.delivery_time,
          }),
          ...(this.formData.location && {
            _location: this.formData.location,
          }),
          ...(this.formData.location_free_input && {
            _location_free_input: this.formData.location_free_input,
          }),
          [this.translateKeyValue('width')]: this.translateKeyValue(this.formData.width + ' cm'),
          [this.translateKeyValue('height')]: this.translateKeyValue(this.formData.height + ' cm'),
          ...(this.formData.color && {
            [this.translateKeyValue('color')]: this.translateKeyValue(this.formData.color),
          }),
          ...(this.formData.pleating && {
            [this.translateKeyValue('pleating')]: this.translateKeyValue(this.formData.pleating),
          }),
          ...(this.formData.partition && {
            [this.translateKeyValue('partition')]: this.translateKeyValue(this.formData.partition),
          }),
          ...(this.formData.sheeting_type && {
            [this.translateKeyValue('sheeting_type')]: this.translateKeyValue(
              this.formData.sheeting_type
            ),
          }),
          ...(this.formData.sheeting_color && {
            [this.translateKeyValue('sheeting_color')]: this.translateKeyValue(
              this.formData.sheeting_color
            ),
          }),
          ...(this.formData.location &&
            !this.formData.location_free_input && {
              [this.translateKeyValue('location')]: this.translateKeyValue(this.formData.location),
            }),
          ...(this.formData.location_free_input && {
            [this.translateKeyValue('location')]: this.translateKeyValue(
              this.formData.location_free_input
            ),
          }),
        },
      };
    }

    /**
     * Handle errors from price calculation
     * @param {Array} errors
     */
    handleErrors(errors) {
      super.handleErrors(errors);

      errors.map((error) => {
        const message = this.translations[error.handle]
          ? this.translations[error.handle]
          : error.message;
        switch (error.type) {
          case 'width':
            if (this.formData.width != '') {
              this.querySelector('[name="width"]').setAttribute('aria-invalid', true);
              this.querySelector('[name="width"]').classList.add('error');
            }
            break;

          case 'height':
            if (this.formData.height != '') {
              this.querySelector('[name="height"]').setAttribute('aria-invalid', true);
              this.querySelector('[name="height"]').classList.add('error');
            }
            break;
        }
      });
    }

    /**
     * Handle variant change
     * @param {HTMLElement} input
     */
    handleVariantChange() {}

    /**
     * Handle next steps
     * @param {HTMLElement} input
     */
    handleSteps(input) {
      const step = input ? input.getAttribute('name') : this.state.step;
      switch (step) {
        case 'width':
          if (this.formData.width != '' && this.formData.height != '') {
            this.setNextStep('pleating');
            this.setProgressBar('size');
          }
          break;
        case 'height':
          if (this.formData.width != '' && this.formData.height != '') {
            this.setNextStep('pleating');
            this.setProgressBar('size');
          }
          break;
        case 'pleating':
          if (this.formData.pleating != '') {
            this.setNextStep('partition');
            this.setProgressBar('pleating');
          }
          break;
        case 'partition':
          if (this.formData.partition != '') {
            if (!this.productData.can_have_sheeting) {
              this.setNextStep('location');
              this.setProgressBar('sheeting');
              this.toggleSubmitButton(true);
            } else {
              this.setNextStep('sheeting_type');
              if (this.formData.sheeting_type == 'none' || this.formData.sheeting_title != '') {
                this.setProgressBar('sheeting');
              } else {
                this.setProgressBar('partition');
              }
            }
          }
          break;
        case 'sheeting_type':
          if (this.formData.sheeting_type == 'none') {
            this.setNextStep('location');
            this.setPrevStep('sheeting_id');
            this.setProgressBar('sheeting');
            // Clear the sheeting placeholder
            this.sheetingPlaceholder.querySelector('input[name="sheeting_title"]').value = '';
            this.sheetingPlaceholder.querySelector('input[name="sheeting_color"]').value = '';
            this.sheetingPlaceholder.querySelector('input[name="sheeting_sku"]').value = '';
            this.toggleSubmitButton(true);
          } else if (this.formData.sheeting_type != '') {
            this.setNextStep('sheeting_id');
            this.setProgressBar('partition');
            this.toggleSubmitButton(false);
            this.sheetingPicker.filter(this.formData.sheeting_type);
          }
          this.sheetingPicker.clear();
          break;
        case 'sheeting_id':
          if (this.formData.sheeting_id != '' && this.formData.sheeting_title != '') {
            this.setNextStep('location');
            this.setProgressBar('sheeting');
            this.toggleSubmitButton(true);
          }
          break;
        case 'location':
          if (
            this.locationFreeInput &&
            handleize(this.formData.location) == this.locationFreeInput
          ) {
            this.setNextStep('location_free_input'); // location_free_input
          } else if (this.formData.location != '') {
            this.setNextStep('quantity'); // quantity
            this.setPrevStep('location_free_input'); // To hide the location free text field
            // Clear the location free text field
            if (this.querySelector('[name="location_free_input"]')) {
              setTimeout(() => {
                this.querySelector('[name="location_free_input"]').value = '';
              }, 300);
            }
          }
          break;
        case 'location_free_input':
          if (this.formData.location_free_input != '') {
            this.setNextStep('quantity');
          }
          break;
      }
    }

    /**
     * Set progress bar step
     */
    setProgressBar(step) {
      if (!step) return;
      if (
        (step == 'size' || step == 'pleating') &&
        (this.state.progressBar == 'partition' || this.state.progressBar == 'sheeting')
      ) {
        return;
      }
      super.setProgressBar(step);
    }
  }

  window.CurtainConfigurator = CurtainConfigurator;

  customElements.define('curtain-configurator', CurtainConfigurator);
}
